'use client';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRef } from 'react';

interface IAboutThePrinceptor {
  title: {
    lineOne: string;
    lineTwo: string;
    lineThree: string;
  };
  cards: {
    text: PortableTextBlock[];
  }[];
}

function StickyCard({
  index,
  total,
  item,
  children,
}: {
  index: number;
  total: number;
  item: { text: PortableTextBlock[] };
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLLIElement>(null);
  const isMobile = useIsMobile({ breakpoint: 992 });
  const isLastCard = index === total - 1;

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: isLastCard
      ? ['start end', 'start center']
      : ['start end', 'start start'],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 0.95, 1]);

  const opacity = useTransform(scrollYProgress, [0, 0.5], [0.6, 1]);

  // Calcula o top baseado no índice para criar o stacking
  const topOffset = 100 + index * 30; // 100px base + 30px por card para efeito de perspectiva

  return (
    <motion.li
      ref={cardRef}
      style={{
        scale,
        opacity,
        position: 'sticky',
        top: `${topOffset}px`,
        transformOrigin: 'center top',
      }}
      className="flex flex-col gap-[3rem] p-[3rem] rounded-[0.5rem] border-[.125rem] text-offwhite border-[rgba(255,255,255,0.08)] prose-h3:text-[3rem] prose-h3:mb-[2rem] prose-p:opacity-70 bg-black min-h-[50rem]"
    >
      {children}
    </motion.li>
  );
}

export default function AboutThePrinceptor({
  title,
  cards,
}: IAboutThePrinceptor) {
  const isMobile = useIsMobile({ breakpoint: 992 });

  return (
    <section className="py-[14rem] lg:py-[28rem] bg-black relative z-10">
      <div className="container max-w-[200rem] items-start flex flex-col gap-[11rem]">
        <motion.div
          initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="flex lg:w-[130rem] w-full flex-col gap-[3.62rem] uppercase "
        >
          <h2 className="text-[6rem] lg:leading-[120%] leading-[110%] lg:text-[9rem] flex flex-col ">
            <span className="block font-[200] text-[rgba(218,218,218,0.7)]">
              {title?.lineOne}
            </span>
            <span className="block font-newsreader italic translate-x-[18%] lg:translate-x-[25rem] font-[300] text-offwhite">
              {title?.lineTwo}
            </span>
            <span className="block font-nunito  font-[200] text-[rgba(218,218,218,0.7)]">
              {title?.lineThree}
            </span>
          </h2>
        </motion.div>

        <ol
          className={`flex justify-center items-stretch  w-full ${
            isMobile === true
              ? 'flex-col relative gap-[10rem]'
              : 'flex-wrap gap-[2.75rem]'
          }`}
        >
          {cards?.map((item, index) => {
            // Renderiza sticky cards apenas no mobile (quando isMobile é true)
            // No desktop ou durante SSR (isMobile null), usa a versão normal
            if (isMobile === true) {
              return (
                <StickyCard
                  key={index}
                  index={index}
                  total={cards.length}
                  item={item}
                >
                  <svg
                    className="w-[5rem]"
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="41"
                    fill="none"
                    viewBox="0 0 40 41"
                  >
                    <rect
                      width="40"
                      height="40"
                      fill="#fff"
                      fillOpacity="0.04"
                      rx="4"
                    ></rect>
                    <rect
                      width="39"
                      height="39"
                      x="0.5"
                      y="0.5"
                      stroke="#DADADA"
                      strokeOpacity="0.16"
                      rx="3.5"
                    ></rect>
                    <g filter="url(#filter0_d_7022_2634)">
                      <path
                        fill="url(#paint0_linear_7022_2634)"
                        d="m31 16.59-.136-.139c-.715.183-2.011.43-3.608.386l-.122.117c.309.705.715 2.193-.78 3.145-1.296.953-2.803.86-3.938-.247-1.656-1.617-2.38-4.24-1.436-5.196.394-.414.881-.632 1.258-.746v-.162A10.6 10.6 0 0 1 20.104 11h-.208a10.7 10.7 0 0 1-2.134 2.748v.162c.377.114.864.332 1.258.746.944.957.22 3.579-1.436 5.196-1.13 1.107-2.638 1.204-3.938.247-1.491-.952-1.089-2.44-.78-3.145l-.122-.117a13.4 13.4 0 0 1-3.608-.386L9 16.59c.246.665.58 1.86.525 3.356l.123.118c1.364-.523 2.35-.012 3.176.515 3.74 2.383 6.056 5.143 7.068 8.422h.207c1.013-3.279 3.33-6.04 7.069-8.422.825-.527 1.816-1.038 3.176-.515l.122-.118a8.9 8.9 0 0 1 .525-3.356zm-10.994 9.719c-.953-1.788-2.032-2.963-3.659-4.536 2.059-.474 3.198-2.302 3.66-3.652.46 1.35 1.6 3.178 3.658 3.652-1.626 1.573-2.706 2.752-3.659 4.536"
                      ></path>
                      <path
                        stroke="url(#paint1_linear_7022_2634)"
                        strokeOpacity="0.4"
                        strokeWidth="0.3"
                        d="M20.015 11.15c.35.647 1.007 1.657 2.065 2.654a3 3 0 0 0-1.206.747c-.268.27-.409.65-.45 1.079s.016.915.154 1.416a6.7 6.7 0 0 0 1.733 2.913c.59.576 1.284.893 2.004.939.72.044 1.456-.184 2.128-.678v-.001c.782-.502 1.077-1.151 1.127-1.773.046-.562-.112-1.09-.26-1.453l.006-.007c1.524.036 2.77-.19 3.5-.37l.005.005a9 9 0 0 0-.507 3.26l-.008.01c-1.383-.493-2.401.038-3.22.56-3.713 2.367-6.046 5.114-7.09 8.382-1.046-3.268-3.378-6.015-7.092-8.382-.818-.522-1.832-1.053-3.218-.56l-.01-.01a9 9 0 0 0-.504-3.253l.01-.012c.73.18 1.977.406 3.5.37l.008.007c-.149.364-.304.892-.258 1.453.05.624.346 1.277 1.133 1.78v-.001c.672.492 1.405.72 2.123.674.72-.045 1.412-.364 2-.94a6.7 6.7 0 0 0 1.734-2.913c.138-.501.195-.988.154-1.416-.036-.375-.149-.713-.355-.973l-.095-.106a3 3 0 0 0-1.207-.747 10.9 10.9 0 0 0 2.065-2.654zm-.15 6.923c-.455 1.329-1.568 3.097-3.551 3.554l-.27.063.2.19c1.622 1.57 2.688 2.733 3.63 4.499l.132.248.133-.247c.941-1.763 2.008-2.93 3.63-4.5l.199-.19-.269-.063c-1.984-.457-3.096-2.225-3.55-3.554l-.142-.415z"
                      ></path>
                    </g>
                    <defs>
                      <linearGradient
                        id="paint0_linear_7022_2634"
                        x1="20"
                        x2="20"
                        y1="11"
                        y2="29"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#F2F0EF"></stop>
                        <stop offset="1" stopColor="#6D6D6D"></stop>
                      </linearGradient>
                      <linearGradient
                        id="paint1_linear_7022_2634"
                        x1="20"
                        x2="20"
                        y1="11"
                        y2="29"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#fff"></stop>
                        <stop
                          offset="1"
                          stopColor="#fff"
                          stopOpacity="0.24"
                        ></stop>
                      </linearGradient>
                      <filter
                        id="filter0_d_7022_2634"
                        width="38"
                        height="34"
                        x="1"
                        y="7"
                        colorInterpolationFilters="sRGB"
                        filterUnits="userSpaceOnUse"
                      >
                        <feFlood
                          floodOpacity="0"
                          result="BackgroundImageFix"
                        ></feFlood>
                        <feColorMatrix
                          in="SourceAlpha"
                          result="hardAlpha"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        ></feColorMatrix>
                        <feOffset dy="4"></feOffset>
                        <feGaussianBlur stdDeviation="4"></feGaussianBlur>
                        <feComposite
                          in2="hardAlpha"
                          operator="out"
                        ></feComposite>
                        <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.72 0"></feColorMatrix>
                        <feBlend
                          in2="BackgroundImageFix"
                          result="effect1_dropShadow_7022_2634"
                        ></feBlend>
                        <feBlend
                          in="SourceGraphic"
                          in2="effect1_dropShadow_7022_2634"
                          result="shape"
                        ></feBlend>
                      </filter>
                    </defs>
                  </svg>

                  {item?.text && (
                    <PortableText
                      value={item.text}
                      components={richTextComponent}
                    />
                  )}
                </StickyCard>
              );
            }

            return (
              <motion.li
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="flex flex-col gap-[3rem] p-[3rem] rounded-[0.5rem] border-[.125rem] text-offwhite max-w-[47.5rem] border-[rgba(255,255,255,0.08)] prose-h3:text-[3rem] prose-h3:mb-[2rem] hover:bg-white/[0.08] transition-colors duration-500 ease-in prose-p:opacity-70"
              >
                <svg
                  className="w-[5rem]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="41"
                  fill="none"
                  viewBox="0 0 40 41"
                >
                  <rect
                    width="40"
                    height="40"
                    fill="#fff"
                    fillOpacity="0.04"
                    rx="4"
                  ></rect>
                  <rect
                    width="39"
                    height="39"
                    x="0.5"
                    y="0.5"
                    stroke="#DADADA"
                    strokeOpacity="0.16"
                    rx="3.5"
                  ></rect>
                  <g filter="url(#filter0_d_7022_2634)">
                    <path
                      fill="url(#paint0_linear_7022_2634)"
                      d="m31 16.59-.136-.139c-.715.183-2.011.43-3.608.386l-.122.117c.309.705.715 2.193-.78 3.145-1.296.953-2.803.86-3.938-.247-1.656-1.617-2.38-4.24-1.436-5.196.394-.414.881-.632 1.258-.746v-.162A10.6 10.6 0 0 1 20.104 11h-.208a10.7 10.7 0 0 1-2.134 2.748v.162c.377.114.864.332 1.258.746.944.957.22 3.579-1.436 5.196-1.13 1.107-2.638 1.204-3.938.247-1.491-.952-1.089-2.44-.78-3.145l-.122-.117a13.4 13.4 0 0 1-3.608-.386L9 16.59c.246.665.58 1.86.525 3.356l.123.118c1.364-.523 2.35-.012 3.176.515 3.74 2.383 6.056 5.143 7.068 8.422h.207c1.013-3.279 3.33-6.04 7.069-8.422.825-.527 1.816-1.038 3.176-.515l.122-.118a8.9 8.9 0 0 1 .525-3.356zm-10.994 9.719c-.953-1.788-2.032-2.963-3.659-4.536 2.059-.474 3.198-2.302 3.66-3.652.46 1.35 1.6 3.178 3.658 3.652-1.626 1.573-2.706 2.752-3.659 4.536"
                    ></path>
                    <path
                      stroke="url(#paint1_linear_7022_2634)"
                      strokeOpacity="0.4"
                      strokeWidth="0.3"
                      d="M20.015 11.15c.35.647 1.007 1.657 2.065 2.654a3 3 0 0 0-1.206.747c-.268.27-.409.65-.45 1.079s.016.915.154 1.416a6.7 6.7 0 0 0 1.733 2.913c.59.576 1.284.893 2.004.939.72.044 1.456-.184 2.128-.678v-.001c.782-.502 1.077-1.151 1.127-1.773.046-.562-.112-1.09-.26-1.453l.006-.007c1.524.036 2.77-.19 3.5-.37l.005.005a9 9 0 0 0-.507 3.26l-.008.01c-1.383-.493-2.401.038-3.22.56-3.713 2.367-6.046 5.114-7.09 8.382-1.046-3.268-3.378-6.015-7.092-8.382-.818-.522-1.832-1.053-3.218-.56l-.01-.01a9 9 0 0 0-.504-3.253l.01-.012c.73.18 1.977.406 3.5.37l.008.007c-.149.364-.304.892-.258 1.453.05.624.346 1.277 1.133 1.78v-.001c.672.492 1.405.72 2.123.674.72-.045 1.412-.364 2-.94a6.7 6.7 0 0 0 1.734-2.913c.138-.501.195-.988.154-1.416-.036-.375-.149-.713-.355-.973l-.095-.106a3 3 0 0 0-1.207-.747 10.9 10.9 0 0 0 2.065-2.654zm-.15 6.923c-.455 1.329-1.568 3.097-3.551 3.554l-.27.063.2.19c1.622 1.57 2.688 2.733 3.63 4.499l.132.248.133-.247c.941-1.763 2.008-2.93 3.63-4.5l.199-.19-.269-.063c-1.984-.457-3.096-2.225-3.55-3.554l-.142-.415z"
                    ></path>
                  </g>
                  <defs>
                    <linearGradient
                      id="paint0_linear_7022_2634"
                      x1="20"
                      x2="20"
                      y1="11"
                      y2="29"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#F2F0EF"></stop>
                      <stop offset="1" stopColor="#6D6D6D"></stop>
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_7022_2634"
                      x1="20"
                      x2="20"
                      y1="11"
                      y2="29"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#fff"></stop>
                      <stop
                        offset="1"
                        stopColor="#fff"
                        stopOpacity="0.24"
                      ></stop>
                    </linearGradient>
                    <filter
                      id="filter0_d_7022_2634"
                      width="38"
                      height="34"
                      x="1"
                      y="7"
                      colorInterpolationFilters="sRGB"
                      filterUnits="userSpaceOnUse"
                    >
                      <feFlood
                        floodOpacity="0"
                        result="BackgroundImageFix"
                      ></feFlood>
                      <feColorMatrix
                        in="SourceAlpha"
                        result="hardAlpha"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      ></feColorMatrix>
                      <feOffset dy="4"></feOffset>
                      <feGaussianBlur stdDeviation="4"></feGaussianBlur>
                      <feComposite in2="hardAlpha" operator="out"></feComposite>
                      <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.72 0"></feColorMatrix>
                      <feBlend
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_7022_2634"
                      ></feBlend>
                      <feBlend
                        in="SourceGraphic"
                        in2="effect1_dropShadow_7022_2634"
                        result="shape"
                      ></feBlend>
                    </filter>
                  </defs>
                </svg>

                {item?.text && (
                  <PortableText
                    value={item.text}
                    components={richTextComponent}
                  />
                )}
              </motion.li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
